import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import AppLayout from './layout/AppLayout';
import { Spinner } from './components';
import ProtectedRoute from './components/ProtectedRoute';
import ProtectedCapabilityRoute from './components/ProtectedCapabilityRoute';
import LoginPage from './features/auth/LoginPage';
import AcceptInvitePage from './features/auth/AcceptInvitePage';
import { ToastContainer } from './components/shared/Toast';

// Eagerly loaded
import TicketList from './features/tickets/TicketList';
import TicketDetail from './features/tickets/TicketDetail';
import ClientProfile from './features/clients/ClientProfile';
import Dashboard from './features/dashboard/Dashboard';
import MetricDrillDown from './features/dashboard/MetricDrillDown';
import UserManagement from './features/admin/UserManagement';
import IntegrationSettings from './features/admin/IntegrationSettings';
import EmailTemplateEditor from './features/admin/EmailTemplateEditor';
import LeadDetail from './features/leads/LeadDetail';
import { TasksPage } from './features/Tasks/TasksPage';

// Lazy loaded
const LeadsList = lazy(() => import('./features/leads/LeadsList'));
const ClientList = lazy(() => import('./features/clients/ClientList'));
const CreateAccountPage = lazy(() => import('./features/accounts/CreateAccountPage').then(m => ({ default: m.CreateAccountPage })));
const EditAccountPage = lazy(() => import('./features/accounts/EditAccountPage').then(m => ({ default: m.EditAccountPage })));
const CreateLeadPage = lazy(() => import('./features/leads/CreateLeadPage').then(m => ({ default: m.CreateLeadPage })));
const EditLeadPage = lazy(() => import('./features/leads/EditLeadPage').then(m => ({ default: m.EditLeadPage })));
const CreateTicketPage = lazy(() => import('./features/tickets/CreateTicketPage').then(m => ({ default: m.CreateTicketPage })));
const EditTicketPage = lazy(() => import('./features/tickets/EditTicketPage').then(m => ({ default: m.EditTicketPage })));
const MarketingMaterials = lazy(() => import('./features/marketing/MarketingMaterials'));
const MeetingsPage = lazy(() => import('./features/meetings/MeetingsPage'));
const ProspectsList = lazy(() => import('./features/prospects/ProspectsList'));
const ProspectDetail = lazy(() => import('./features/prospects/ProspectDetail'));

// Catalog
const CatalogLayout = lazy(() => import('./features/catalog/index'));
const SuppliersPage = lazy(() => import('./features/catalog/suppliers/SuppliersPage'));
const LocationsPage = lazy(() => import('./features/catalog/locations/LocationsPage'));
const CategoriesPage = lazy(() => import('./features/catalog/categories/CategoriesPage'));
const ConcernTagsPage = lazy(() => import('./features/catalog/concern-tags/ConcernTagsPage'));
const PackageTypesPage = lazy(() => import('./features/catalog/package-types/PackageTypesPage'));
const BasesPage = lazy(() => import('./features/catalog/bases/BasesPage'));
const ActivesPage = lazy(() => import('./features/catalog/actives/ActivesPage'));
const FragrancesPage = lazy(() => import('./features/catalog/fragrances/FragrancesPage'));
const FragranceTypesPage = lazy(() => import('./features/catalog/fragrance-types/FragranceTypesPage'));
const PackagingPage = lazy(() => import('./features/catalog/packaging/PackagingPage'));
const GeneratedSkusPage = lazy(() => import('./features/catalog/generated-skus/GeneratedSkusPage'));
const LabelsPage = lazy(() => import('./features/catalog/labels/LabelsPage'));
const CatalogFilesPage = lazy(() => import('./features/catalog/files/FilesPage'));
const SkuHistoryPage = lazy(() => import('./features/catalog/sku-history/SkuHistoryPage'));
const PackagingSetsPage = lazy(() => import('./features/catalog/packaging-sets/PackagingSetsPage'));
const OrdersPage = lazy(() => import('./features/orders/OrdersPage'));
const OrderDetailsPage = lazy(() => import('./features/orders/OrderDetailsPage'));
const PartnersListPage = lazy(() => import('./features/partners/PartnersListPage'));
const PartnerDetailsPage = lazy(() => import('./features/partners/PartnerDetailsPage'));
const RevenuePlanningPage = lazy(() => import('./features/revenue-planning/RevenuePlanningPage'));
const AgentPromptsPage = lazy(() => import('./features/agents/AgentPromptsPage'));
const KnowledgeArticlesPage = lazy(() => import('./features/agents/KnowledgeArticlesPage'));
const AgentInboxPage = lazy(() => import('./features/agents/AgentInboxPage'));
const AgentConversationPage = lazy(() => import('./features/agents/AgentConversationPage'));
const AgentAuditQueuePage = lazy(() => import('./features/agents/AgentAuditQueuePage'));
const AgentDatasetsPage = lazy(() => import('./features/agents/AgentDatasetsPage'));
const AgentOperationsPage = lazy(() => import('./features/agents/AgentOperationsPage'));

const IS_NON_PROD = window.location.hostname !== 'crm.internal.cre8orglobal.com';

export default function App() {
  return (
    <>
      {IS_NON_PROD && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
          height: '15px', background: '#facc15',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '10px', fontWeight: 600, color: '#78350f', letterSpacing: '0.05em',
          userSelect: 'none',
        }}>
          You are working on a test/dev environment
        </div>
      )}
      <ToastContainer />
      <Suspense fallback={<div className="flex items-center justify-center h-screen"><Spinner size="lg" /></div>}>
      <Routes>
        {/* Public routes — accessible without a token */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />

        {/* All other routes require a valid, non-expired Google JWT */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AppLayout />}>
          {/* Default redirect */}
          <Route index element={<Navigate to="/leads" replace />} />

          {/* Active routes */}
          <Route path="/leads" element={<LeadsList />} />
          <Route path="/tasks" element={<TasksPage />} />
          <Route path="/leads/create" element={<CreateLeadPage />} />
          <Route path="/leads/:id/edit" element={<EditLeadPage />} />
          <Route path="/leads/:id" element={<LeadDetail />} />
          <Route path="/prospects" element={<ProspectsList />} />
          <Route path="/prospects/:id" element={<ProspectDetail />} />
          <Route path="/tickets" element={<TicketList />} />
          <Route path="/tickets/create" element={<CreateTicketPage />} />
          <Route path="/tickets/:id/edit" element={<EditTicketPage />} />
          <Route path="/tickets/:id" element={<TicketDetail />} />
          <Route path="/clients" element={<ClientList />} />
          <Route path="/clients/:id/edit" element={<EditAccountPage />} />
          <Route path="/clients/:id" element={<ClientProfile />} />
          <Route path="/accounts/create" element={<CreateAccountPage />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/dashboard/:metricKey" element={<MetricDrillDown />} />
          <Route path="/revenue-planning" element={<RevenuePlanningPage />} />

          {/* Admin routes */}
          <Route path="/admin/users" element={<UserManagement />} />
          <Route path="/admin/prompts" element={<Navigate to="/agents/prompts" replace />} />
          <Route path="/admin/integrations" element={<IntegrationSettings />} />
          <Route path="/admin/email-templates" element={<EmailTemplateEditor />} />

          {/* CRM-owned Agent Platform */}
          <Route element={<ProtectedCapabilityRoute requiredCapabilities={['agents.read']} />}>
            <Route path="/agents/inbox" element={<AgentInboxPage />} />
            <Route path="/agents/conversations/:conversationId" element={<AgentConversationPage />} />
            <Route path="/agents/prompts" element={<AgentPromptsPage />} />
            <Route path="/agents/knowledge" element={<KnowledgeArticlesPage />} />
          </Route>
          <Route element={<ProtectedCapabilityRoute requiredCapabilities={['agents.audit']} />}>
            <Route path="/agents/audits" element={<AgentAuditQueuePage />} />
          </Route>
          <Route element={<ProtectedCapabilityRoute requiredCapabilities={['agents.datasets.review']} />}>
            <Route path="/agents/datasets" element={<AgentDatasetsPage />} />
          </Route>
          <Route element={<ProtectedCapabilityRoute requiredCapabilities={['agents.operations']} />}>
            <Route path="/agents/operations" element={<AgentOperationsPage />} />
          </Route>

          {/* Marketing routes */}
          <Route path="/marketing-materials" element={<MarketingMaterials />} />

          {/* Meetings routes */}
          <Route path="/meetings" element={<MeetingsPage />} />

          {/* Catalog routes — CatalogMng or Admin */}
          <Route element={<ProtectedCapabilityRoute allowedRoles={[3, 5]} />}>
            <Route path="/catalog" element={<CatalogLayout />}>
              <Route index element={<Navigate to="/catalog/generated-skus" replace />} />
              <Route path="suppliers" element={<SuppliersPage />} />
              <Route path="locations" element={<LocationsPage />} />
              <Route path="categories" element={<CategoriesPage />} />
              <Route path="concern-tags" element={<ConcernTagsPage />} />
              <Route path="package-types" element={<PackageTypesPage />} />
              <Route path="bases" element={<BasesPage />} />
              <Route path="actives" element={<ActivesPage />} />
              <Route path="fragrances" element={<FragrancesPage />} />
              <Route path="fragrance-types" element={<FragranceTypesPage />} />
              <Route path="packaging" element={<PackagingPage />} />
              <Route path="packaging-sets" element={<PackagingSetsPage />} />
              <Route path="generated-skus" element={<GeneratedSkusPage />} />
              <Route path="labels" element={<LabelsPage />} />
              <Route path="files" element={<CatalogFilesPage />} />
              <Route path="sku-history" element={<SkuHistoryPage />} />
            </Route>
          </Route>

          {/* Legacy redirects */}
          <Route path="/support" element={<Navigate to="/tickets" replace />} />
          <Route path="/crm" element={<Navigate to="/clients" replace />} />
          <Route path="/crm/:clientId" element={<Navigate to="/clients" replace />} />

          {/* Orders — OrdersMng or Admin */}
          <Route element={<ProtectedCapabilityRoute allowedRoles={[3, 6]} requiredCapabilities={['orders.read']} />}>
            <Route path="/orders" element={<OrdersPage />} />
            <Route path="/orders/:orderId" element={<OrderDetailsPage />} />
          </Route>

          {/* Partners — PartnersMng or Admin */}
          <Route element={<ProtectedCapabilityRoute allowedRoles={[3, 4]} />}>
            <Route path="/partners" element={<PartnersListPage />} />
            <Route path="/partners/:partnerId" element={<PartnerDetailsPage />} />
          </Route>

          {/* Coming Soon URL redirects */}
          <Route path="/ai-studio" element={<Navigate to="/leads" replace />} />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/leads" replace />} />
        </Route>
        </Route>
      </Routes>
      </Suspense>
    </>
  );
}
